import { Button, Divider, Grid, Group, LoadingOverlay, Stack, Title } from '@mantine/core';
import { useQueryClient } from '@tanstack/react-query';

import { Declaration, DeclarationGoods } from '@e-origin/shared';

import { EucdmCode, MultiLangCodes, NumberInput, TextInput } from '@components';
import { useDeclarant } from '@hooks/declarant';
import { useCodeListSystem } from '@hooks/useCodeListSystem';
import { useDeclarationViewGoodLVForm } from './useDeclarationViewGoodLVForm';

interface DeclarationViewGoodLVProps {
  editDisabled?: boolean;
  declaration: Declaration;
  goodDetails: DeclarationGoods;
  onHide(): void;
}

export const DeclarationViewGoodLV: React.FC<DeclarationViewGoodLVProps> = ({ declaration, goodDetails, editDisabled, onHide }) => {
  const { data: declarant, isLoading } = useDeclarant();
  const queryClient = useQueryClient();
  const { form, onSubmit, isSaving } = useDeclarationViewGoodLVForm({
    declaration,
    goodDetails,
    onHide,
  });

  const codelistSystems = useCodeListSystem({
    group: declaration?.generalInfo?.group,
    isExport: declaration?.generalInfo?.isExport,
  });

  const onSubmitForm = async (values) => {
    await onSubmit(values);
    queryClient.invalidateQueries({ queryKey: ['declaration', declaration._id] });
  };

  if (!declarant) {
    return <LoadingOverlay visible={isLoading} overlayProps={{ radius: 'sm', blur: 2 }} />;
  }

  return (
    <form onSubmit={form.onSubmit(onSubmitForm)}>
      <Stack m="xl">
        <Grid my="sm">
          <Grid.Col span={6}>
            <EucdmCode path="goodsShipmentItems.referenceNumberUCR">
              <TextInput {...form.getInputProps('referenceNumberUCR')} label="Reference number UCR" disabled={editDisabled} />
            </EucdmCode>
          </Grid.Col>
          <Grid.Col span={6}>
            <EucdmCode path="goodsShipmentItems.additionalProcedure">
              <MultiLangCodes
                {...form.getInputProps('additionalProcedure')}
                multiple
                searchable
                clearable
                hidePickedOptions
                label="Additional procedure"
                code="CL457"
                system={codelistSystems}
                onChange={(value) => form.setFieldValue('additionalProcedure', value)}
                disabled={editDisabled}
              />
            </EucdmCode>
          </Grid.Col>
        </Grid>

        <Grid mb="xl">
          <Grid.Col span={12}>
            <EucdmCode path="goodsShipmentItems.descriptionOfGoods">
              <TextInput {...form.getInputProps('descriptionOfGoods')} label="Description of goods" disabled={editDisabled} />
            </EucdmCode>
          </Grid.Col>
        </Grid>

        <Divider mb="md" />

        <Stack mb="sm">
          <Title order={5} fw={600}>
            Commodity code
          </Title>

          <Grid mb="sm">
            <Grid.Col span={12}>
              <EucdmCode path="goodsShipmentItems.commodityCode.hsCode">
                <TextInput
                  {...form.getInputProps('commodityCode.hsCode')}
                  label="HS Code"
                  onChange={(event) => {
                    form.setFieldValue('commodityCode.hsCode', event.target.value?.trim());
                  }}
                  disabled={editDisabled}
                />
              </EucdmCode>
            </Grid.Col>
          </Grid>
        </Stack>

        <Divider mb="md" />

        <Stack mb="ms">
          <Title order={5} fw={600}>
            Goods measure
          </Title>

          <Grid mb="sm">
            <Grid.Col span={6}>
              <EucdmCode path="goodsShipmentItems.goodsMeasure.intrinsicValue">
                <NumberInput
                  {...form.getInputProps('goodsMeasure.intrinsicValue')}
                  decimalScale={2}
                  decimalSeparator="."
                  label="Intrinsic value"
                  disabled={editDisabled}
                />
              </EucdmCode>
            </Grid.Col>
            <Grid.Col span={6}>
              <EucdmCode path="goodsShipmentItems.goodsMeasure.currencyCode">
                <TextInput {...form.getInputProps('goodsMeasure.currencyCode')} label="Currency code" disabled={editDisabled} />
              </EucdmCode>
            </Grid.Col>
          </Grid>

          <Grid>
            <Grid.Col span={6}>
              <EucdmCode path="goodsShipmentItems.goodsMeasure.grossMass">
                <NumberInput {...form.getInputProps('goodsMeasure.grossMass')} label="Gross mass" disabled={editDisabled} />
              </EucdmCode>
            </Grid.Col>
            <Grid.Col span={6}>
              <EucdmCode path="goodsShipmentItems.goodsMeasure.supplementaryUnits">
                <NumberInput {...form.getInputProps('goodsMeasure.supplementaryUnits')} label="Supplementary units" disabled={editDisabled} />
              </EucdmCode>
            </Grid.Col>
          </Grid>
        </Stack>

        <Divider mb="md" />

        <Stack mb="xl">
          <Title order={5} fw={600}>
            Transport and insurance costs
          </Title>

          <Grid>
            <Grid.Col span={6}>
              <EucdmCode path="goodsShipmentItems.transportInsurance.currencyCode">
                <TextInput {...form.getInputProps('transportInsurance.currencyCode')} label="Currency code" disabled={editDisabled} />
              </EucdmCode>
            </Grid.Col>
            <Grid.Col span={6}>
              <EucdmCode path="goodsShipmentItems.transportInsurance.amount">
                <NumberInput
                  {...form.getInputProps('transportInsurance.amount')}
                  decimalScale={2}
                  decimalSeparator="."
                  label="Amount"
                  disabled={editDisabled}
                />
              </EucdmCode>
            </Grid.Col>
          </Grid>
        </Stack>

        <Divider mb="md" />

        <Stack mb="xl">
          <Title order={5} fw={600}>
            Packaging
          </Title>

          <Grid>
            <Grid.Col span={12}>
              <EucdmCode path="goodsShipmentItems.packaging.numberOfPackages">
                <NumberInput {...form.getInputProps('packaging.numberOfPackages')} label="Number of packages" disabled={editDisabled} />
              </EucdmCode>
            </Grid.Col>
          </Grid>
        </Stack>

        <Divider mb="md" />

        <Stack mb="sm">
          <Title order={5} fw={600}>
            Duties & Taxes
          </Title>

          <Grid>
            <Grid.Col span={12}>
              <TextInput {...form.getInputProps('dutiesAndTaxes.methodOfPayment')} label="Method of payment" disabled={editDisabled} />
            </Grid.Col>
          </Grid>
        </Stack>
      </Stack>

      <Divider mb="xl" />

      <Group justify="center" my="md">
        <Button loading={isSaving} type="submit" key="save-modal-btn" variant="filled" size="lg">
          Save
        </Button>
      </Group>
    </form>
  );
};
